import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { employeeMenuItems } from '../components/employee/employeeMenu';
import { managerMenuItems } from '../components/manager/managerMenu';
import {
  ExpenseEventRecord,
  getExpenseEvents,
  isExpenseApiError,
  uploadExpenseReceipt,
} from '../services/expenseService';

const formatINR = (amount: number): string => `₹${Math.round(amount).toLocaleString('en-IN')}`;
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const guessContentType = (file: File): string => {
  const type = String(file.type || '').trim();
  if (type) return type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
};

const toBase64WithoutPrefix = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const InvoiceUploadPage: React.FC = () => {
  const location = useLocation();
  const isManagerView = location.pathname.startsWith('/manager/');
  const [events, setEvents] = useState<ExpenseEventRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [fileByEventId, setFileByEventId] = useState<Record<string, File | null>>({});

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const rows = await getExpenseEvents({ perPage: 500 });
        setEvents(rows);
      } catch (err) {
        if (isExpenseApiError(err)) setError(err.message);
        else if (err instanceof Error) setError(err.message);
        else setError('Failed to load past expenses.');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [events]);

  const onPickFile = (eventId: string, file: File | null) => {
    setFileByEventId((prev) => ({ ...prev, [eventId]: file }));
  };

  const onUpload = async (eventId: string) => {
    const file = fileByEventId[eventId];
    if (!file) {
      setStatusMessage('Please choose a file before upload.');
      return;
    }
    try {
      setUploadingId(eventId);
      setStatusMessage(null);
      const dataBase64 = await toBase64WithoutPrefix(file);
      await uploadExpenseReceipt(eventId, {
        file_name: file.name,
        content_type: guessContentType(file),
        data_base64: dataBase64,
        receipt_metadata: {},
      });
      setStatusMessage('Invoice uploaded successfully.');
      setFileByEventId((prev) => ({ ...prev, [eventId]: null }));
      const rows = await getExpenseEvents({ perPage: 500 });
      setEvents(rows);
    } catch (err) {
      if (isExpenseApiError(err)) setStatusMessage(err.message);
      else if (err instanceof Error) setStatusMessage(err.message);
      else setStatusMessage('Invoice upload failed.');
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <AdminShell
      pageTitle="Invoice Upload"
      subtitle="Upload receipts/invoices for past expense events."
      menuItems={isManagerView ? managerMenuItems : employeeMenuItems}
      profileName={isManagerView ? 'Approving Manager' : 'Employee'}
      profileRoleTag={isManagerView ? 'MANAGER' : 'EMPLOYEE'}
    >
      <article className="rounded-[16px] border border-[#cde4ef] bg-white px-5 py-4">
        <p className="text-[12px] font-semibold text-[#547688]">
          Select a past expense and upload invoice. Files are stored in Supabase bucket and linked to the event.
        </p>
      </article>

      {error ? (
        <p className="rounded-[12px] border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-semibold text-red-700">
          {error}
        </p>
      ) : null}
      {statusMessage ? (
        <p className="rounded-[12px] border border-[#d4e6ef] bg-[#f4fbff] px-4 py-3 text-[13px] font-semibold text-[#17566e]">
          {statusMessage}
        </p>
      ) : null}

      <article className="overflow-hidden rounded-[16px] border border-[#d8e8f0] bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-[13px]">
            <thead className="bg-[#f3fbff] text-[#5b8194]">
              <tr>
                <th className="px-4 py-3 font-bold">Date</th>
                <th className="px-4 py-3 font-bold">Merchant</th>
                <th className="px-4 py-3 font-bold">Category</th>
                <th className="px-4 py-3 font-bold">Amount</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Invoice Upload</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-5 font-semibold text-[#6f8a9a]" colSpan={6}>
                    Loading past expenses...
                  </td>
                </tr>
              ) : sortedEvents.length === 0 ? (
                <tr>
                  <td className="px-4 py-5 font-semibold text-[#6f8a9a]" colSpan={6}>
                    No past expenses found.
                  </td>
                </tr>
              ) : (
                sortedEvents.map((row) => {
                  const selected = fileByEventId[row.id];
                  const isUploading = uploadingId === row.id;
                  return (
                    <tr key={row.id} className="border-t border-[#edf4f8]">
                      <td className="px-4 py-3 font-semibold text-[#355769]">{formatDate(row.created_at)}</td>
                      <td className="px-4 py-3 font-semibold text-[#355769]">{row.merchant || '-'}</td>
                      <td className="px-4 py-3 font-semibold text-[#355769]">{row.category || '-'}</td>
                      <td className="px-4 py-3 font-bold text-[#0f6f8a]">{formatINR(Number(row.total_amount || 0))}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-[#e9f6fb] px-2.5 py-1 text-[11px] font-bold text-[#0d6f89]">
                          {String(row.aggregate_status || 'PENDING')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            onChange={(e) => onPickFile(row.id, e.target.files?.[0] || null)}
                            className="max-w-[220px] text-[12px]"
                          />
                          <button
                            type="button"
                            disabled={!selected || isUploading}
                            onClick={() => onUpload(row.id)}
                            className="rounded-full bg-[#0d7d99] px-3 py-1.5 text-[12px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isUploading ? 'Uploading...' : 'Upload'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </article>
    </AdminShell>
  );
};

export default InvoiceUploadPage;
